import React, { useEffect, useState } from 'react'
import { Modal, Form } from 'react-bootstrap'

const emptyLink = { text: '', url: '' }

const LinkEditorModal = ({
  show,
  onHide,
  links = [],
  onChange,
  editIndex,
  selectedText = '',
  selectedStart = 0,
}) => {
  const [localLinks, setLocalLinks] = useState([])
  const [current, setCurrent] = useState(emptyLink)
  const [editIdx, setEditIdx] = useState(null)
  const [error, setError] = useState('')

  // Note: we intentionally do NOT sync localLinks from props on every render
  // to avoid clearing the just-added row when parent re-renders.

  useEffect(() => {
    if (typeof editIndex === 'number' && editIndex >= 0) {
      setEditIdx(editIndex)
      setCurrent(links[editIndex] || emptyLink)
    } else {
      setEditIdx(null)
      // Check if selection overlaps any existing link by position
      let matchedByPosition = null
      if (Array.isArray(links)) {
        matchedByPosition = links.find((link) => {
          if (
            typeof link?.startIndex === 'number' &&
            typeof link?.length === 'number'
          ) {
            const start = link.startIndex
            const end = start + link.length
            return selectedStart >= start && selectedStart < end
          }
          return false
        })
      }

      if (matchedByPosition) {
        // Prefill both text and url for the specific occurrence
        setCurrent({
          text: selectedText || matchedByPosition.text || '',
          url: matchedByPosition.url || '',
        })
      } else {
        // No positional match → treat as new link; do not copy url from same word elsewhere
        setCurrent({ text: selectedText || '', url: '' })
      }
    }
  }, [editIndex, links, selectedText, selectedStart])

  const handleSaveCurrent = () => {
    if (
      !current.text ||
      !current.text.trim() ||
      !current.url ||
      !current.url.trim()
    ) {
      setError('Both fields are required')
      return
    }

    // Check if this is an update to an existing link
    const isUpdatingExistingLink = links.some(
      (link) => link.text === current.text,
    )

    const next = [...localLinks]
    if (editIdx !== null) {
      next[editIdx] = { ...current }
    } else {
      next.push({ ...current })
    }
    setLocalLinks(next)

    // Pass additional info to parent about whether this is an update
    const ok = onChange ? onChange([current], isUpdatingExistingLink) : true
    if (ok) {
      setCurrent(emptyLink)
      setEditIdx(null)
      setError('')
      // Close the modal after successful save
      onHide()
    } else {
      setError('Failed to insert link')
    }
  }

  const isEdit = editIdx !== null || (current.url && current.url.trim())

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop='static'
      keyboard={false}
      dialogClassName='experience-confirm-dialog link-editor-modal'
      contentClassName='experience-confirm-card'
      className='link-editor-overlay'
      style={{ zIndex: 9999 }}>
      <Modal.Body className='p-0'>
        <div className='experience-confirm-body'>
          <h2>{isEdit ? 'Edit link' : 'Add link'}</h2>
          <p className='experience-confirm-lead'>
            Select text in the question or instruction, then set the label and
            URL participants will open.
          </p>
          <Form>
            <div className='experience-f mb-3'>
              <Form.Label className='experience-f-label'>Link text</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter link text (or use selected text)'
                value={current.text}
                className='experience-f-control'
                onChange={(e) => {
                  const value = e.target.value
                  setCurrent((c) => ({ ...c, text: value }))
                  if (error) setError('')
                }}
              />
            </div>
            <div className='experience-f mb-3'>
              <Form.Label className='experience-f-label'>Link URL</Form.Label>
              <Form.Control
                type='url'
                placeholder='https://example.com'
                value={current.url}
                className='experience-f-control'
                onChange={(e) => {
                  const value = e.target.value
                  setCurrent((c) => ({ ...c, url: value }))
                  if (error) setError('')
                }}
              />
            </div>
            {error ? (
              <div className='text-danger mb-2 small'>{error}</div>
            ) : null}
          </Form>
          <div className='experience-modal-actions'>
            <button
              type='button'
              className='btn-outline-experience'
              onClick={onHide}>
              Cancel
            </button>
            <button
              type='button'
              className='btn-navy'
              onClick={handleSaveCurrent}>
              {editIdx !== null ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default LinkEditorModal
